/* eslint-disable @typescript-eslint/no-this-alias */
define((require, exports, module) => {
    'use strict';

    const xTabId = require('xTabId');
    const xPageId = require('xPageId');
    const csrfToken = require('system/csrfToken');
    const getBase58Id = require('system/libs/getBase58Id');
    const currentUser = require('system/currentUser');
    const MicroEvent = require('mrg-microevent');
    const env = require('../env');
    const delay = require('../async/delay');
    const s3fs = require('../s3/s3fs');
    const fileSystemFileReader = require('../fs/fileSystemFileReader');
    const UploadReason = require('../../UploadReason');
    const UploadCancel = require('../../UploadCancel');
    const HttpError = require('../../errors/HttpError');
    const IllegalContentFail = require('../../fails/IllegalContentFail');
    const FileTooLargeError = require('../../errors/FileTooLargeError');
    const ConnectionFail = require('../../fails/ConnectionFail');
    const UploaderData = require('./UploaderData');

    const MB = 1024 * 1024;

    /**
     * @constructor
     * @extends {MicroEvent}
     * @param {File} params.file
     * @param {string} params.url
     * @param {string} params.ACL
     */
    function Uploader({ file, url, ACL }) {
        MicroEvent.call(this);

        this._file = file;
        this._url = url;
        this._ACL = ACL;
    }

    Object.assign(Uploader, {
        /**
         * Событие прогресса загрузки файла.
         * @static
         * @type {string}
         */
        EVENT_PROGRESS: 'progress',

        /**
         * Ошибка загрузки файла, после которой будет выполнена повторная попытка.
         * @static
         * @type {string}
         */
        EVENT_RETRY: 'retry',

        /**
         * HTTP-метод загузки файла.
         * @type {string}
         */
        HTTP_METHOD: 'PUT',

        /**
         * Максимальное количество попыток повторения загрузки.
         * @static
         * @type {string}
         */
        MAX_RETRIES_COUNT: 3,

        /**
         * Максимальный размер файла, для которого
         * можно повторять попытки загрузки  (в мегабайтах).
         * @static
         * @type {string}
         */
        MAX_RETRYABLE_SIZE: 300 * MB,

        /**
         * Время задержки между повторами в миллисекундах (в миллисекундах).
         * @static
         * @type {number}
         */
        RETRY_DELAY: 1000,

        /**
         * Увеличивать время ожидания между повторными попытками загрузки.
         * @static
         * @type {boolean}
         */
        IS_INCREMENTAL_RETRY_DELAY: true,

        /**
         * Время задержки между EVENT_PROGRESS (в миллисекундах).
         * @static
         * @type {number}
         */
        PROGRESS_DELAY: 300,

        /**
         * @static
         * @type {Array.<number>}
         */
        retryableHttpStatuses: [
            ConnectionFail.CONNECTION_FAILURE,
            HttpError.REQUEST_TIMEOUT,
            HttpError.INTERNAL_SERVER_ERROR,
            HttpError.BAD_GATEWAY,
            HttpError.SERVICE_UNAVAILABLE,
            HttpError.GATEWAY_TIMEOUT,
        ],

        /**
         * @static
         * @param {number} status – HTTP status
         * @returns {boolean}
         */
        isRetryableHttpStatus(status) {
            return Uploader.retryableHttpStatuses.includes(status);
        },

        /**
         * @static
         * @param {number} readyState – xhr.readyState
         * @returns {boolean}
         */
        isCancelableXhrState(readyState) {
            const UNSENT = XMLHttpRequest.UNSENT;
            const DONE = XMLHttpRequest.DONE;
            return UNSENT < readyState && readyState < DONE;
        },
    });

    Uploader.prototype = Object.assign(Object.create(MicroEvent.prototype), {
        constructor: Uploader,

        /**
         * Адрес сервера для загрузки файла, полученный от балансера.
         * @type {string}
         */
        _url: '',

        /**
         * The canned ACL to apply to the object.
         * @type {string}
         */
        _ACL: '',

        /**
         * Номер попытки загрузки файла.
         * @type {number}
         */
        _attempt: 0,

        /**
         * Резолвит промис, который возвращает метод upload.
         * @type {Function}
         */
        _resolve: null,

        /**
         * Реджектит промис, который возвращает метод upload.
         * @type {Function}
         */
        _reject: null,

        /**
         * @type {File}
         */
        _file: null,

        /**
         * @type {Promise}
         */
        _retryDelay: null,

        /**
         * @type {number}
         */
        _onXhrProgressCallTime: 0,

        /**
         * @type {number}
         */
        _uploadStartTime: -1,

        /**
         * @returns {boolean}
         */
        _canRetry() {
            const hasRetryableSize =
                this._file.size <= Uploader.MAX_RETRYABLE_SIZE;
            const hasAttempt = this._attempt < Uploader.MAX_RETRIES_COUNT;
            return hasRetryableSize && hasAttempt;
        },

        /**
         * @returns {UploaderData}
         */
        _getData() {
            const xhr = this._xhr;
            const params = {
                url: this._url,
                attempt: this._attempt,
                size: this._file.size,
            };

            if (xhr) {
                params.status = xhr.status;
                params.responseText = xhr.responseText;
            }

            return new UploaderData(params);
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrAbort(event, data) {
            const source = UploadReason.SOURCE_WEB_CLIENT;
            const stack = new Error('UploadCancel');
            const reason = new UploadCancel(stack, source);
            const retryDelay = this._retryDelay;

            if (retryDelay) {
                retryDelay.cancel(reason);
            }

            data.error = reason;
            this._fail(data);
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrError(event, data) {
            const status = data.status;
            const source = UploadReason.SOURCE_BACKEND;
            let stack;
            let reason;

            if (status === HttpError.ILLEGAL) {
                stack = new Error('IllegalContentFail');
                reason = new IllegalContentFail(stack, source);
            } else if (status === HttpError.TOO_LARGE) {
                stack = new Error('FileTooLargeError');
                reason = new FileTooLargeError(stack, source);
            } else if (status) {
                stack = new Error('HttpError');
                reason = new HttpError(
                    stack,
                    source,
                    status,
                    data.responseText,
                );
            } else {
                stack = new Error('ConnectionFail');
                reason = new ConnectionFail(stack, source);
            }

            data.error = reason;

            const isRetryable = Uploader.isRetryableHttpStatus(status);
            const canRentry = this._canRetry();

            if (isRetryable && canRentry) {
                this._retry(data);
            } else {
                this._fail(data);
            }
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrLoad(event, data) {
            const HTTP_OK = 200;
            const HTTP_CREATED = 201;
            const status = data.status;

            if (status === HTTP_OK || status === HTTP_CREATED) {
                const kilobytes = data.size / 1024;
                const seconds = (Date.now() - this._uploadStartTime) / 1000;

                data.speed = parseFloat((kilobytes / seconds).toFixed(1));
                this._done(data);
            } else {
                this._onXhrError({}, data);
            }
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrProgress(event, data) {
            const now = Date.now();

            if (now - this._onXhrProgressCallTime > Uploader.PROGRESS_DELAY) {
                this._onXhrProgressCallTime = now;

                const total = event.total;
                const loaded = event.loaded;
                let progress;

                if (total && loaded) {
                    progress = (loaded * 100) / total;
                    progress = Math.round(progress);
                    progress = Math.min(progress, 100);
                } else {
                    progress = 0;
                }

                data.progress = progress;
                this.emit(Uploader.EVENT_PROGRESS, data);
            }
        },

        /**
         * @param {Event} event
         */
        handleEvent(event) {
            const uploader = this;
            const promise = new Promise((resolve) => {
                // @type {UploaderData}
                const data = uploader._getData();

                switch (event.type) {
                    case 'abort':
                        uploader._onXhrAbort(event, data);

                        break;

                    case 'error':
                        uploader._onXhrError(event, data);

                        break;

                    case 'load':
                        uploader._onXhrLoad(event, data);

                        break;

                    case 'progress':
                        uploader._onXhrProgress(event, data);

                        break;
                }

                resolve();
            });

            promise.catch(function(reason) {
                this.onCatch(reason);
            });
        },

        /**
         * @param {XMLHttpRequest} xhr
         */
        _listenXhr(xhr) {
            xhr.addEventListener('abort', this);
            xhr.addEventListener('error', this);
            xhr.addEventListener('load', this);
            xhr.upload.addEventListener('progress', this);
        },

        /**
         * @param {XMLHttpRequest} xhr
         */
        _stopListeningXhr(xhr) {
            xhr.removeEventListener('abort', this);
            xhr.removeEventListener('error', this);
            xhr.removeEventListener('load', this);
            xhr.upload.removeEventListener('progress', this);
        },

        onCatch(reason) {
            const data = this._getData();

            data.error = reason;
            this._fail(data);
        },

        /**
         * @param {UploaderData} data
         */
        _retry(data) {
            const uploader = this;

            const xhr = uploader._xhr;

            if (xhr) {
                uploader._stopListeningXhr(xhr);
            }

            this._attempt++;

            const RETRY_DELAY = Uploader.RETRY_DELAY;
            let promise;

            if (RETRY_DELAY) {
                if (Uploader.IS_INCREMENTAL_RETRY_DELAY) {
                    promise = delay(RETRY_DELAY * this._attempt);
                } else {
                    promise = delay(RETRY_DELAY);
                }
            } else {
                promise = Promise.resolve();
            }

            uploader._retryDelay = promise;
            promise = promise.then(
                () => {
                    uploader._retryDelay = null;
                    uploader.emit(Uploader.EVENT_RETRY, data);
                },
                (reason) => {
                    uploader._retryDelay = null;

                    return Promise.reject(reason);
                },
            );

            // Повторяет загрузку файла.
            promise = promise.then(() => {
                uploader._upload();
            });

            promise = promise.catch(function(reason) {
                this.onCatch(reason);
            });
        },

        /**
         * @param {File|FormData} body
         */
        _makeRequest(body) {
            const uploader = this;
            return new Promise((resolve) => {
                // TODO: use some standard method like RPC.call that handles all infrastructure logic inside
                const xReqId = getBase58Id(8);
                const xEmail = currentUser.get('email');
                const xCsrfToken = csrfToken.get('token');
                const xhr = new XMLHttpRequest();

                uploader._xhr = xhr;

                xhr.open(Uploader.HTTP_METHOD, uploader._url);
                xhr.withCredentials = true;
                xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                xhr.setRequestHeader('x-email', xEmail);
                xhr.setRequestHeader('x-req-id', xReqId);
                xhr.setRequestHeader('x-tab-id', xTabId);
                xhr.setRequestHeader('x-page-id', xPageId);
                xhr.setRequestHeader('x-csrf-token', xCsrfToken);
                xhr.setRequestHeader('x-amz-acl', uploader._ACL);

                uploader._listenXhr(xhr);
                uploader._uploadStartTime = Date.now();

                xhr.send(body);
                resolve();
            });
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploaderData}
         */
        _upload() {
            const uploader = this;
            let promise = Promise.resolve(uploader._file);

            // Проверяет, что файл до сих пор доступен в локальной ФС.
            promise = promise.then((file) => {
                let promise;

                if (file.size > fileSystemFileReader.MAX_READABLE_SIZE) {
                    promise = Promise.resolve(file);
                } else {
                    // Выполняем проверку только для небольших файлов,
                    // чтобы не получить исключение "out of memory".
                    promise = fileSystemFileReader.readFile(file);
                }

                return promise;
            });

            // Проверяет, удовлетворяет ли файл ограничениям Объектного хранилища:
            // • Не больше 32GB.
            // • Не превышает лимит пользователя.
            promise = promise.then((file) => {
                return s3fs.checkFileSizeLessThanLimit(file.size);
            });

            // Проверяет, удовлетворяет ли файл ограничениям браузера:
            // • Не больше 4GB в IE.
            promise = promise.then((size) => {
                return env.checkFileSizeLessThanLimit(size);
            });

            // Проверяет, достаточно ли места в бакете.
            promise = promise.then((size) => {
                return s3fs.checkEnoughSpace(size);
            });

            // Выполняет загрузку файла.
            promise = promise.then(() => {
                return uploader._makeRequest(uploader._file);
            });

            promise.catch((reason) => {
                const data = uploader._getData();

                data.error = reason;
                uploader._fail(data);
            });
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploaderData}
         */
        upload() {
            const uploader = this;
            return new Promise((resolve, reject) => {
                uploader._resolve = resolve;
                uploader._reject = reject;
                uploader._upload();
            });
        },

        cancel() {
            const data = this._getData();
            const xhr = this._xhr;

            if (xhr) {
                const readyState = xhr.readyState;
                const isCancelable = Uploader.isCancelableXhrState(readyState);

                if (isCancelable) {
                    xhr.abort();
                } else if (readyState !== XMLHttpRequest.DONE) {
                    this._onXhrAbort({}, data);
                }
            } else {
                this._onXhrAbort({}, data);
            }
        },

        /**
         * @param {UploaderData} data
         */
        _done(data) {
            this._resolve(data);
            this._destroy();
        },

        /**
         * @param {UploaderData} data
         */
        _fail(data) {
            if (this._reject) {
                this._reject(data);
                this._destroy();
            }
        },

        _destroy() {
            const xhr = this._xhr;

            if (xhr) {
                this._stopListeningXhr(xhr);

                if (Uploader.isCancelableXhrState(xhr.readyState)) {
                    xhr.abort();
                }

                this._xhr = null;
            }

            this._resolve = null;
            this._reject = null;
            this._file = null;
        },
    });

    module.exports = Uploader;
});
